/* eslint-disable no-console */
import { db, timestamp } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from "firebase/firestore";

const state = {
  articleWatchStatus: null, // Tracks if current user has watched current article
  loadingWatchStatus: false
};

const actions = {
  /**
   * Check if a member has already watched an article
   * @param {Object} context - Vuex context
   * @param {Object} payload - Contains programId, articleId, memberId
   * @returns {Promise<boolean>} - True if already watched, false otherwise
   */
  async checkArticleWatchStatus({ commit, getters }, payload) {
    const { programId, articleId, memberId } = payload;

    if (!programId || !articleId || !memberId) {
      console.error("Missing required parameters for checkArticleWatchStatus");
      return false;
    }

    commit("setLoadingWatchStatus", true);

    try {
      // Check if the watch record exists in the article's watched subcollection
      const programRef = doc(db(), "programs", programId);
      const articlesRef = collection(programRef, "articles");
      const articleRef = doc(articlesRef, articleId);
      const watchedRef = collection(articleRef, "watched");
      const watchRef = doc(watchedRef, memberId);

      const watchDoc = await getDoc(watchRef);
      const hasWatched = watchDoc.exists();

      console.log(
        `Article ${articleId} watch status for member ${memberId}: ${
          hasWatched ? "Already watched" : "Not watched yet"
        }`
      );

      commit("setArticleWatchStatus", hasWatched);
      commit("setLoadingWatchStatus", false);

      return hasWatched;
    } catch (error) {
      console.error("Error checking article watch status:", error);
      commit("setLoadingWatchStatus", false);
      return false;
    }
  },

  /**
   * Record that a member has watched an article
   * @param {Object} context - Vuex context
   * @param {Object} payload - Contains programId, articleId, memberId, title (optional), pointsAwarded (optional)
   * @returns {Promise<boolean>} - Success status
   */
  async recordArticleWatch({ commit, dispatch }, payload) {
    const { programId, articleId, memberId, title, pointsAwarded } = payload;

    if (!programId || !articleId || !memberId) {
      console.error("Missing required parameters for recordArticleWatch");
      return false;
    }

    try {
      // Double-check if already watched before proceeding
      const alreadyWatched = await dispatch("checkArticleWatchStatus", {
        programId,
        articleId,
        memberId
      });

      if (alreadyWatched) {
        console.log(
          `Member ${memberId} has already watched article ${articleId}. Not recording again.`
        );
        return false;
      }

      // Create a record in the article's watched subcollection
      const programRef = doc(db(), "programs", programId);
      const articlesRef = collection(programRef, "articles");
      const articleRef = doc(articlesRef, articleId);
      const watchedRef = collection(articleRef, "watched");
      const watchRef = doc(watchedRef, memberId);

      await setDoc(watchRef, {
        memberId,
        watchedAt: serverTimestamp(),
        articleTitle: title || "",
        pointsAwarded: Number(pointsAwarded) || 0, // Store points awarded for audit purposes
        createdAt: serverTimestamp() // Add a created timestamp to distinguish from updates
      });

      console.log(
        `Successfully recorded watch for member ${memberId} on article ${articleId} with ${pointsAwarded ||
          0} points`
      );
      commit("setArticleWatchStatus", true);
      return true;
    } catch (error) {
      console.error("Error recording article watch:", error);
      return false;
    }
  }
};

const mutations = {
  setArticleWatchStatus(state, status) {
    state.articleWatchStatus = status;
  },
  setLoadingWatchStatus(state, status) {
    state.loadingWatchStatus = status;
  }
};

const getters = {
  articleWatchStatus(state) {
    return state.articleWatchStatus;
  },
  loadingWatchStatus(state) {
    return state.loadingWatchStatus;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
